import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, ArrowPathIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

const MIN_DICE = 1;
const MAX_DICE = 6;

/* ── Pip positions for a 3×3 grid (row, col) ── */
const PIP_POSITIONS: Record<number, [number, number][]> = {
  1: [[1, 1]],
  2: [[0, 2], [2, 0]],
  3: [[0, 2], [1, 1], [2, 0]],
  4: [[0, 0], [0, 2], [2, 0], [2, 2]],
  5: [[0, 0], [0, 2], [1, 1], [2, 0], [2, 2]],
  6: [[0, 0], [0, 2], [1, 0], [1, 2], [2, 0], [2, 2]],
};

const DiceFace: React.FC<{ value: number }> = ({ value }) => {
  const pips = PIP_POSITIONS[value] ?? [];
  return (
    <div className="w-20 h-20 bg-surface rounded-xl shadow-lg grid grid-cols-3 grid-rows-3 p-2.5">
      {Array.from({ length: 9 }, (_, i) => {
        const row = Math.floor(i / 3);
        const col = i % 3;
        const hasPip = pips.some(([r, c]) => r === row && c === col);
        return (
          <div key={i} className="flex items-center justify-center">
            {hasPip && <div className="w-3.5 h-3.5 rounded-full bg-stone-800" />}
          </div>
        );
      })}
    </div>
  );
};

const DiceRollerPage: React.FC = () => {
  const navigate = useNavigate();
  const diceRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [phase, setPhase] = useState<'ready' | 'rolling' | 'revealed'>('ready');
  const [numDice, setNumDice] = useState(2);
  const [results, setResults] = useState<number[]>([]);

  const goBack = useCallback(() => navigate(-1), [navigate]);

  const handleRoll = useCallback(() => {
    setPhase('rolling');

    const newResults = Array.from({ length: numDice }, () => Math.floor(Math.random() * 6) + 1);
    setResults(newResults);

    // Animate each die
    let maxDuration = 0;
    diceRefs.current.forEach((ref, i) => {
      if (!ref) return;
      ref.getAnimations().forEach((a) => a.cancel());

      const rx = 720 + Math.random() * 360;
      const rz = 360 + Math.random() * 360;
      const duration = 800 + i * 50;
      maxDuration = Math.max(maxDuration, duration);

      ref.animate(
        [
          { transform: 'rotateX(0) rotateZ(0) scale(1)', opacity: '0.6' },
          { transform: `rotateX(${rx}deg) rotateZ(${rz}deg) scale(1.15)`, offset: 0.4, opacity: '1' },
          { transform: 'rotateX(0) rotateZ(0) scale(1)', opacity: '1' },
        ],
        { duration, easing: 'ease-out', fill: 'forwards' },
      );
    });

    setTimeout(() => setPhase('revealed'), maxDuration);
  }, [numDice]);

  const handleRollAgain = useCallback(() => {
    diceRefs.current.forEach((ref) => ref?.getAnimations().forEach((a) => a.cancel()));
    requestAnimationFrame(() => handleRoll());
  }, [handleRoll]);

  const total = results.reduce((sum, v) => sum + v, 0);

  return (
    <div className="fixed inset-0 z-50 bg-stone-900 flex flex-col items-center justify-center">
      {/* Close Button */}
      <button
        onClick={goBack}
        className="absolute top-4 right-4 z-20 p-2 bg-stone-100/10 hover:bg-primary-500/20 rounded-full text-white transition-colors"
      >
        <XMarkIcon className="h-8 w-8" />
      </button>

      <div className="flex flex-col items-center gap-8">
        <h1 className="text-white text-fluid-h1 font-bold font-serif">Dice Roller</h1>

        {/* Dice count picker */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setNumDice((n) => Math.max(MIN_DICE, n - 1))}
            disabled={numDice <= MIN_DICE || phase === 'rolling'}
            className="w-9 h-9 rounded-full flex items-center justify-center border border-stone-500 text-stone-300 hover:bg-stone-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <MinusIcon className="h-4 w-4" />
          </button>
          <span className="text-white text-lg font-bold w-16 text-center">
            {numDice} {numDice === 1 ? 'die' : 'dice'}
          </span>
          <button
            onClick={() => setNumDice((n) => Math.min(MAX_DICE, n + 1))}
            disabled={numDice >= MAX_DICE || phase === 'rolling'}
            className="w-9 h-9 rounded-full flex items-center justify-center border border-stone-500 text-stone-300 hover:bg-stone-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Dice display area */}
        <div className="flex flex-wrap items-center justify-center gap-4 min-h-[6rem]">
          {Array.from({ length: numDice }, (_, i) => (
            <div
              key={i}
              ref={(el) => {
                diceRefs.current[i] = el;
              }}
            >
              {results[i] != null ? (
                <DiceFace value={results[i]} />
              ) : (
                <div className="w-20 h-20 rounded-xl border-2 border-dashed border-stone-600" />
              )}
            </div>
          ))}
        </div>

        {/* Result total */}
        {phase === 'revealed' && results.length > 1 && (
          <div className="bg-amber-50 px-8 py-4 rounded-2xl shadow-xl border border-stone-200">
            <span className="text-4xl font-black font-serif text-stone-900">{total}</span>
          </div>
        )}

        {/* Single-die result */}
        {phase === 'revealed' && results.length === 1 && (
          <div className="bg-amber-50 px-8 py-4 rounded-2xl shadow-xl border border-stone-200">
            <span className="text-4xl font-black font-serif text-stone-900">{results[0]}</span>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-4">
          {phase === 'revealed' && (
            <button
              onClick={handleRollAgain}
              className="flex items-center gap-2 px-5 py-3 bg-stone-600 hover:bg-stone-700 text-white rounded-2xl font-bold text-base shadow-2xl transition hover:scale-105 active:scale-95"
            >
              <ArrowPathIcon className="h-5 w-5" />
              Roll Again
            </button>
          )}
          {phase !== 'revealed' && (
            <button
              onClick={handleRoll}
              disabled={phase === 'rolling'}
              className="px-8 py-4 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-2xl font-bold text-xl shadow-2xl transition hover:scale-105 active:scale-95 disabled:scale-100"
            >
              {phase === 'rolling' ? 'Rolling...' : 'Roll!'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiceRollerPage;
